import { getDataProperty } from '../utils/_domFunctions';
import { handleErrors } from '../utils/_handleErrors';
import { collectRefs, ensureIdAttribute } from '../utils/_webComponents';

const IdTemplates = {
    radio: (idBase) => `${idBase}-radio`,
    editionSection: (idBase) => `${idBase}-edition-section`,
    editionLoader: (idBase) => `${idBase}-edition-loader`,
    editionError: (idBase) => `${idBase}-edition-error`,
};

class mAddressSelectorCard extends HTMLDivElement {
    static observedAttributes = ['data-address-id', 'data-mode'];

    constructor() {
        super();
        collectRefs.call(this);
        ensureIdAttribute.call(this);

        // 'default' or 'addition'. 'addition' is used to show a new address form
        this.mode = 'mode' in this.dataset ? this.dataset.mode : 'default';

        this.setupAccessibility();
        this.setSummaryView();

        this.refs.editButton?.addEventListener('click', this.setEditionView.bind(this));
        this.refs.cancelEditButtonTop?.addEventListener('click', this.setSummaryView.bind(this));
        this.refs.cancelEditButton?.addEventListener('click', this.setSummaryView.bind(this));
        this.refs.deleteButton?.addEventListener('click', this.openRemoveModal.bind(this));
    }

    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {
            case 'data-address-id':
                this.refs.inputRadio.value = newValue;
                break;
            case 'data-mode':
                // Changing mode doesn't change translations
                this.mode = newValue;
                break;
            default:
                break;
        }
    }

    /**
     * Generate ID attributes and ARIA attributes for accessibility usage
     */
    setupAccessibility() {
        // Radio input
        if (!this.refs.inputRadio.id) {
            this.refs.inputRadio.id = IdTemplates.radio(this.id);
        }

        // Input label
        if (!this.refs.inputLabel.hasAttribute('for')) {
            this.refs.inputLabel.setAttribute('for', this.refs.inputRadio.id);
        }

        // Edition section
        if (!this.refs.editionSection.id) {
            this.refs.editionSection.id = IdTemplates.editionSection(this.id);
        }
        this.refs.editionSection.setAttribute('aria-live', 'off');
        this.refs.editionSection.setAttribute('aria-busy', 'false');
        this.refs.editionSection.setAttribute('tabindex', -1);

        // Edition section buttons
        this.refs.editButton?.setAttribute('aria-expanded', 'false');
        this.refs.editButton?.setAttribute('aria-controls', this.refs.editionSection.id);
        this.refs.cancelEditButton?.setAttribute('aria-expanded', 'true');
        this.refs.cancelEditButton?.setAttribute('aria-controls', this.refs.editionSection.id);

        // Edition loader
        if (this.refs.editionLoader) {
            if (!this.refs.editionLoader.id) {
                this.refs.editionLoader.id = IdTemplates.editionLoader(this.id);
            }
        }

        // Edition error
        if (this.refs.editionError) {
            if (!this.refs.editionError.id) {
                this.refs.editionError.id = IdTemplates.editionError(this.id);
            }
        }
    }

    /**
     * Set edition panel loading state
     * @param {boolean} isLoading Current edition panel loading state
     */
    setEditionLoadingState(isLoading) {
        if (!this.refs.editionLoader) {
            return;
        }
        if (isLoading) {
            this.refs.editionSection.setAttribute('aria-busy', 'true');
            this.refs.editionSection.setAttribute('aria-live', 'polite');
            this.refs.editionSection.classList.add('loading');
            this.refs.editionSection.setAttribute('aria-describedby', this.refs.editionLoader.id);
        } else {
            if (!this.refs.editionSection.getAttribute('aria-describedby') === this.refs.editionLoader.id) {
                this.refs.editionSection.removeAttribute('aria-describedby');
            }
            this.refs.editionSection.classList.remove('loading');
            this.refs.editionSection.setAttribute('aria-busy', 'false');
            this.refs.editionSection.setAttribute('aria-live', 'off');
        }
    }

    /**
     * Set edition panel error state
     * @param {boolean} isError Current edition panel error state
     */
    setEditionErrorState(isError) {
        if (!this.refs.editionError) {
            return;
        }
        this.refs.editionSection.classList.toggle('error', isError);
        if (isError) {
            this.refs.editionSection.setAttribute('aria-describedby', this.refs.editionError.id);
        } else {
            this.refs.editionSection.removeAttribute('aria-describedby');
        }
    }

    /**
     * Get an URL to fetch an address edition form
     * @returns {URL} Generated URL object
     */
    getEditFormUrl() {
        const editFormUrl = new URL(getDataProperty(this, 'editFormUrl', 'string'));

        // addressId params ; no param means a new address
        if (this.mode !== 'addition') {
            const addressId = getDataProperty(this, 'addressId', 'string');
            editFormUrl.searchParams.set('addressId', addressId);
        }

        // stage params
        const currentStage = document.querySelector('[data-checkout-stage]')?.dataset.checkoutStage;
        if (currentStage) {
            editFormUrl.searchParams.set('stage', currentStage);
        }
        return editFormUrl;
    }

    /**
     * Update component view to summary view
     * This method is called externally
     * @fires mAddressSelectorCard#closecardedition
     */
    setSummaryView() {
        const editionClosed = this.view === 'edition';
        this.view = 'summary';
        const moveFocus = this.refs.editionSection.contains(document.activeElement);
        this.classList.remove('address-card-edition-mode');
        this.classList.add('address-card-summary-mode');
        if (this.refs.editionFormContainer) {
            this.refs.editionFormContainer.innerHTML = '';
        }
        // Move focus
        if (moveFocus) {
            this.refs.editButton?.focus();
            this.refs.editButton?.scrollIntoView({ block: 'center' });
        }
        if (editionClosed) {
            /**
             * Close event. Triggered when the address edition is closing.
             * @event mAddressSelectorCard#closecardedition
             */
            this.dispatchEvent(new CustomEvent('closecardedition', { bubbles: true }));
        }
    }

    /**
     * Update component view to edition view
     * This method is called externally
     * @fires mAddressSelectorCard#opencardedition
     */
    setEditionView() {
        this.view = 'edition';
        const moveFocus = document.activeElement === this.refs.editButton;
        this.classList.add('address-card-edition-mode');
        this.classList.remove('address-card-summary-mode');
        /**
         * Open event. Triggered when the address edition was saved successfully.
         * @event mAddressSelectorCard#savecardedition
         */
        this.dispatchEvent(new CustomEvent('opencardedition', { bubbles: true }));
        this.setEditionErrorState(false);
        this.setEditionLoadingState(true);
        this.refs.editionFormContainer.innerHTML = '';

        if (moveFocus) {
            this.refs.editionSection.parentElement.focus();
            this.refs.editionSection.parentElement.scrollIntoView({ block: "start" });
        }

        fetch(this.getEditFormUrl(), { method: 'GET' })
            .then(handleErrors)
            .then((response) => response.text())
            .then((formHTMLText) => {
                // TODO: Use a custom controller with a custom template to avoid this transformation
                const container = document.createElement('div');
                container.innerHTML = formHTMLText;
                const form = container.querySelector('form');
                this.refs.editionFormContainer.append(form);
                form.addEventListener('checkout:addAddress', this.onEditionSubmit.bind(this));
            })
            .catch(() => {
                this.setEditionErrorState(true);
            })
            .finally(() => {
                this.setEditionLoadingState(false);
            });
    }

    /**
     * Update address card with new data
     * This method is called externally
     * @param {string} newAddressId New address ID
     * @param {string} summaryHTML New address card Summary as HTML string
     */
    updateAddressCard(newAddressId, summaryHTML) {
        // Update address ID
        if (this.mode !== 'addition') {
            this.dataset.addressId = newAddressId;
        }
        // Update address card summary
        if (this.refs.addressSummary) {
            this.refs.addressSummary.innerHTML = summaryHTML || '';
        }
    }

    /**
     * Callback event for when the submit button is clicked
     * @param {Event} event event from the listener
     * @fires mAddressSelectorCard#savecardedition
     */
    onEditionSubmit(event) {
        const form = event.currentTarget;
        const currentStage = document.querySelector('[data-checkout-stage]')?.dataset.checkoutStage;
        const oldAddressId = this.dataset.addressId;
        const saveAddressUrl = new URL(form.action);
        saveAddressUrl.searchParams.set('stage', currentStage);
        fetch(saveAddressUrl, {
            method: 'POST',
            body: new FormData(form),
        })
            .then(handleErrors)
            .then((response) => response.json())
            .then((body) => {
                if (body.error || !body.success) {
                    throw new Error(body.message || 'Unknown error. Please try again');
                }
                const newAddressId = body.addressId;
                this.updateAddressCard(newAddressId, body.templates?.addressSelectorCardSummary);
                this.setSummaryView();
                /**
                 * Save event. Triggered when the address was saved successfully.
                 * @event mAddressSelectorCard#savecardedition
                 * @type {object}
                 * @property {object} detail Event detail property
                 * @property {string} detail.oldAddressId Previous address ID
                 * @property {string} detail.newAddressId New address ID
                 * @property {object} detail.templates Contains useful templates from back-end
                 */
                this.dispatchEvent(new CustomEvent('savecardedition', {
                    bubbles: true,
                    detail: {
                        oldAddressId,
                        newAddressId,
                        templates: body.templates,
                    },
                }));
            })
            .catch((error) => {
                form.setFormMessage('error', error.message);
            });
    }

    /**
     * Open and setup modal to remove address from address book
     * @param {Event} event event from the listener
     * @fires mAddressSelectorCard#removecard
     */
    openRemoveModal() {
        const removeModal = document.getElementById('m-popin-remove-item-address');
        if (!removeModal) {
            throw new Error('Missing remove modal');
        }

        const confirmDelete = removeModal.querySelector('[data-component-confirm-delete]');
        if (!confirmDelete) {
            throw new Error('Missing delete button in remove modal');
        }

        const onDelete = () => {
            fetch(this.dataset.deleteUrl, { method: 'GET' })
                .then(handleErrors);
            /**
             * Remove event. Triggered when the address was removed.
             * @event mAddressSelectorCard#removecard
             * @type {object}
             * @property {object} detail Event detail property
             * @property {string} detail.addressId Address ID
             */
            this.dispatchEvent(new CustomEvent('removecard', {
                bubbles: true,
                detail: {
                    addressId: this.dataset.addressId,
                },
            }));
        };
        confirmDelete.addEventListener('click', onDelete);
        removeModal.addEventListener('close', () => {
            /*
             * close event will be called first because our click event listener on
             * the delete button is placed after the popin listener that trigger the close event.
             * Thus we use a timeout 0 here to trigger the following action after our listener.
             * Note: This is a reliable solution, not really a hack
             */
            setTimeout(() => {
                confirmDelete.removeEventListener('click', onDelete);
            }, 0);
        }, { once: true });
        removeModal.togglePopin();
    }
}

customElements.define('m-address-selector-card', mAddressSelectorCard, {
    extends: 'div',
});
